import { useState } from "react";
import React from "react";
import {
  Box, List, ListItemButton, ListItemIcon, ListItemText,
  Paper, Popper, ClickAwayListener, Tooltip,
  useTheme, useMediaQuery,
} from "@mui/material";
import {
  AccountTreeRounded, DataObjectOutlined,
  SensorsRounded, RouterRounded, ManageAccountsRounded, SecurityRounded,
  BusinessRounded, AccountTreeOutlined, Group, GroupsRounded,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import DevicesMenuIcon from "./icons/DevicesMenuIcon";
import HomeIcon from "./icons/HomeIcon";
import useAuth from "../../hoc/AuthProvider/useAuth";
import { Modules, Permissions } from "../../config/permissions";

const SIDEBAR_BG = "#1a2035";
const FLYOUT_BG = "#243054";
const ACTIVE_BG = "var(--color-primary)";
const HOVER_BG = "rgba(255,255,255,0.07)";
const TEXT_COLOR = "rgba(255,255,255,0.85)";
const ICON_COLOR = "rgba(255,255,255,0.6)";

type ChildItem = {
  label: string;
  icon: React.ReactNode;
  path: string;
  permission?: string;
  module?: string;
};

type MenuItem = {
  label: string;
  icon: React.ReactNode;
  path?: string;
  permission?: string;
  module?: string;
  children?: ChildItem[];
};

const menuItems: MenuItem[] = [
  { label: "Dashboard", icon: <HomeIcon />, path: "/Dashboard", permission: Permissions.Dashboard_View, module: Modules.IOT_CORE },
  {
    label: "Cihazlar",
    icon: <DevicesMenuIcon />,
    permission: Permissions.Device_View,
    module: Modules.IOT_CORE,
    children: [
      { label: "Tüm Cihazlar", icon: <DevicesMenuIcon />, path: "/Devices", permission: Permissions.Device_View, module: Modules.IOT_CORE },
      { label: "Sensör Cihazlar", icon: <SensorsRounded />, path: "/Devices?deviceTypeId=1", permission: Permissions.Device_View, module: Modules.IOT_CORE },
      { label: "Gateway Cihazlar", icon: <RouterRounded />, path: "/Devices?deviceTypeId=2", permission: Permissions.Device_View, module: Modules.IOT_CORE },
      { label: "Cihaz Gruplarım", icon: <GroupsRounded />, path: "/MyDeviceGroups", permission: Permissions.Device_View, module: Modules.IOT_CORE },
    ],
  },
  {
    label: "Parserlar",
    icon: <AccountTreeRounded />,
    permission: Permissions.ParserConfig_View,
    module: Modules.IOT_CORE,
    children: [
      { label: "Sensör Parserlar", icon: <SensorsRounded />, path: "/SensorParsers", permission: Permissions.ParserConfig_View, module: Modules.IOT_CORE },
      { label: "Gateway Parserlar", icon: <RouterRounded />, path: "/GatewayParsers", permission: Permissions.ParserConfig_View, module: Modules.IOT_CORE },
    ],
  },
  { label: "Sensör Simüle", icon: <DataObjectOutlined />, path: "/ParserSimulate", permission: Permissions.Simulate_View, module: Modules.IOT_CORE },
  {
    label: "Yönetim",
    icon: <ManageAccountsRounded />,
    module: Modules.IOT_CORE,
    children: [
      { label: "Kullanıcılar", icon: <ManageAccountsRounded />, path: "/Users", permission: Permissions.User_View, module: Modules.IOT_CORE },
      { label: "Roller", icon: <SecurityRounded />, path: "/Roles", permission: Permissions.Role_View, module: Modules.IOT_CORE },
      { label: "Şirketler", icon: <BusinessRounded />, path: "/Companies", permission: Permissions.Company_View, module: Modules.IOT_CORE },
      { label: "Departmanlar", icon: <AccountTreeOutlined />, path: "/Departments", permission: Permissions.Department_View, module: Modules.IOT_CORE },
      { label: "Cihaz Grup Ayarları", icon: <Group />, path: "/DeviceGroups", permission: Permissions.DeviceGroup_View, module: Modules.IOT_CORE },
    ],
  },
];

type SidebarProps = {
  open: boolean;
  onClose: () => void;
};

const MenuList = ({ open, onNavigate }: { open: boolean; onNavigate: () => void }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [flyoutMenu, setFlyoutMenu] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { havePermission, haveModule } = useAuth();

  const canShow = (permission?: string, module?: string) => {
    if (!permission) return true;
    return havePermission(permission) && (module ? haveModule(module) : true);
  };

  const visibleMenuItems = menuItems
    .map((item) => {
      if (!item.children) return item;
      const visibleChildren = item.children.filter((c) => canShow(c.permission, c.module));
      return { ...item, children: visibleChildren };
    })
    .filter((item) => {
      if (item.children) return item.children.length > 0;
      return canShow(item.permission, item.module);
    });

  const currentFullPath = location.pathname + location.search;

  const isChildActive = (item: MenuItem) =>
    item.children?.some((c) => currentFullPath === c.path) ?? false;

  const handleItemClick = (item: MenuItem, el: HTMLElement) => {
    if (item.children) {
      if (flyoutMenu === item.label) {
        setFlyoutMenu(null);
        setAnchorEl(null);
      } else {
        setAnchorEl(el);
        setFlyoutMenu(item.label);
      }
    } else if (item.path) {
      navigate(item.path);
      setFlyoutMenu(null);
      setAnchorEl(null);
      onNavigate();
    }
  };

  return (
    <List disablePadding sx={{ pt: 1 }}>
      {visibleMenuItems.map((item) => {
        const hasChildren = !!item.children?.length;
        const isActive = item.path ? location.pathname === item.path : false;
        const childActive = isChildActive(item);
        const highlighted = isActive || childActive;
        const isFlyoutOpen = flyoutMenu === item.label;

        return (
          <Box key={item.label}>
            <Tooltip title={open ? "" : item.label} placement="right">
              <ListItemButton
                onClick={(e) => handleItemClick(item, e.currentTarget)}
                sx={{
                  py: 1.4,
                  px: open ? 2 : 1,
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1.5,
                  justifyContent: open ? "flex-start" : "center",
                  backgroundColor: highlighted ? ACTIVE_BG : "transparent",
                  "&:hover": { backgroundColor: highlighted ? ACTIVE_BG : HOVER_BG },
                }}
              >
                <ListItemIcon sx={{ minWidth: open ? 36 : "unset", color: highlighted ? "#fff" : ICON_COLOR }}>
                  {item.icon}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={item.label}
                    slotProps={{
                      primary: {
                        fontSize: 14,
                        color: highlighted ? "#fff" : TEXT_COLOR,
                        fontWeight: highlighted ? 600 : 400,
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>

            {hasChildren && (
              <Popper
                open={isFlyoutOpen}
                anchorEl={anchorEl}
                placement="right-start"
                sx={{ zIndex: 1400 }}
                modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
              >
                <ClickAwayListener onClickAway={() => { setFlyoutMenu(null); setAnchorEl(null); }}>
                  <Paper
                    elevation={8}
                    sx={{
                      backgroundColor: FLYOUT_BG,
                      borderRadius: 2,
                      overflow: "hidden",
                      minWidth: 210,
                      py: 0.5,
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <List disablePadding>
                      {item.children!.map((child) => {
                        const isChildItemActive = currentFullPath === child.path;
                        return (
                          <ListItemButton
                            key={child.path}
                            onClick={() => {
                              const [pathname, search] = child.path.split("?");
                              navigate({ pathname, search: search ? `?${search}` : "" });
                              setFlyoutMenu(null);
                              setAnchorEl(null);
                              onNavigate();
                            }}
                            sx={{
                              py: 1.2,
                              px: 2,
                              mx: 0.5,
                              my: 0.2,
                              borderRadius: 1,
                              backgroundColor: isChildItemActive ? ACTIVE_BG : "transparent",
                              "&:hover": { backgroundColor: isChildItemActive ? ACTIVE_BG : HOVER_BG },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32, color: isChildItemActive ? "#fff" : ICON_COLOR }}>
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.label}
                              slotProps={{
                                primary: {
                                  fontSize: 13,
                                  color: isChildItemActive ? "#fff" : TEXT_COLOR,
                                  fontWeight: isChildItemActive ? 600 : 400,
                                },
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Paper>
                </ClickAwayListener>
              </Popper>
            )}
          </Box>
        );
      })}
    </List>
  );
};

const Sidebar = ({ open, onClose }: SidebarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <Box
          onClick={onClose}
          sx={{
            display: open ? "block" : "none",
            position: "fixed",
            top: 64,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            zIndex: 1200,
          }}
        />
        {/* Slide-in panel */}
        <Box
          sx={{
            position: "fixed",
            top: 64,
            left: 0,
            bottom: 0,
            width: { xs: "80vw", sm: 220 },
            backgroundColor: SIDEBAR_BG,
            zIndex: 1201,
            transform: open ? "translateX(0)" : "translateX(-100%)",
            transition: "transform 0.25s ease",
            overflowY: "auto",
          }}
        >
          <MenuList open={true} onNavigate={onClose} />
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        width: open ? 220 : 60,
        minHeight: "100%",
        backgroundColor: SIDEBAR_BG,
        flexShrink: 0,
        transition: "width 0.2s ease",
      }}
    >
      <MenuList open={open} onNavigate={() => { }} />
    </Box>
  );
};

export default Sidebar;
